import errorHandler from './utils/errorHandler';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import api from './axiosConfig';
import * as Yup from 'yup';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormGroup,
  Tabs,
  Tab,
  Chip,
  IconButton,
  InputAdornment,
  Tooltip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonalInfo from './PersonalInfo';
import Holidays from './Holidays';
import Absences from './Absences';
import TrainingManagement from './TrainingManagement';
import Disciplinary from './Disciplinary';
import Notes from './Notes';
import ReviewForm from './ReviewForm';
import EmployeeScheduleEditor from './EmployeeScheduleEditor';
import ContractDialog from './ContractDialog';

const initialEmployeeState = {
  first_name: '',
  last_name: '',
  name: '',
  email: '',
  role: '',
  job_role: '',
  tier_id: '',
  hourly_rate: '',
  address_line1: '',
  address_line2: '',
  address_line3: '',
  postcode: '',
  contact_number: '',
  emergency_contact: '',
  emergency_number: '',
  date_of_birth: '',
  national_insurance_number: '',
  start_date: '',
  active: true,
  commission_services: '',
  commission_products: '',
  full_time_equivalent: 1,
  generates_revenue: true
};

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().nullable(),
  email: Yup.string().email('Invalid email').required('Email is required'),
  role: Yup.string().required('Role is required'),
  hourly_rate: Yup.number().positive('Hourly rate must be positive').required('Hourly rate is required'),
  address_line1: Yup.string().required('Address line 1 is required'),
  postcode: Yup.string().required('Postcode is required'),
  contact_number: Yup.string().required('Contact number is required'),
  date_of_birth: Yup.date().required('Date of birth is required'),
  national_insurance_number: Yup.string().required('National Insurance number is required'),
  start_date: Yup.date().required('Start date is required'),
  generates_revenue: Yup.boolean(),
});

const EmployeeCard = () => {
  const { user, refreshAccountId } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(initialEmployeeState);
  const [originalEmployee, setOriginalEmployee] = useState({});
  const [errors, setErrors] = useState({});
  const [mode, setMode] = useState('View');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState('PersonalInfo');
  const [isLoading, setIsLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [reviewMode, setReviewMode] = useState('view');
  const [userAccountId, setUserAccountId] = useState(null);

  // Pricing tiers — fetched once on mount from /api/calculator/tiers
  const [tiers, setTiers] = useState([]);
  
  // Login creation state
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loginCreating, setLoginCreating] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Employment contract dialog
  const [contractOpen, setContractOpen] = useState(false);

  // Fetch pricing tiers for this account
  const fetchTiers = useCallback(async () => {
    try {
      const response = await api.get('/calculator/tiers');
      setTiers(response.data || []);
    } catch (error) {
      // Non-fatal — tier dropdown just shows empty with helper text
      console.warn('Could not fetch pricing tiers:', error);
      setTiers([]);
    }
  }, []);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  // Updated function to fetch the account ID with force refresh option
  const fetchUserAccountId = useCallback(async (forceRefresh = false) => {
    try {
      // If we already have account_id and not forcing refresh, use it
      if (!forceRefresh && user?.account_id) {
        console.log('Using cached account_id from user context:', user.account_id);
        setUserAccountId(user.account_id);
        return user.account_id;
      }

      console.log('Fetching fresh account_id from API...');
      // Use the /user/account endpoint instead of /user/profile
      const response = await api.get('/user/account');
      errorHandler.logDebug('EmployeeCard', 'User account response:', response.data);
      
      if (response.data && response.data.account_id) {
        const accountId = response.data.account_id;
        setUserAccountId(accountId);
        
        // If account_id differs from what's in user context, log it and refresh
        if (user?.account_id && user.account_id !== accountId) {
          console.log('Account ID mismatch detected, refreshing AuthContext:', {
            cached: user.account_id,
            fresh: accountId
          });
          // Trigger AuthContext refresh if available
          if (refreshAccountId) {
            await refreshAccountId();
          }
        }
        
        return accountId;
      }
    } catch (error) {
      console.error('Error fetching user account ID:', error);
      // Don't set a hardcoded fallback - let it remain null
      setUserAccountId(null);
    }
    return null;
  }, [user, refreshAccountId]);

  // Modified useEffect to force refresh account ID when component mounts
  useEffect(() => {
    // Always force refresh account ID to ensure we have the latest
    fetchUserAccountId(true);
  }, [fetchUserAccountId]);

  const getRoleOptions = useCallback(() => {
    const { role: currentUserRole } = user;
    
    // Base role options that everyone can assign
    let allowedRoles = [
      { value: 'ACCOUNT_USER', label: 'Account User' }
    ];

    // Add additional roles based on user's role
    if (currentUserRole === 'ADMIN') {
      allowedRoles = [
        { value: 'ADMIN', label: 'Admin' },
        { value: 'ACCOUNT_OWNER', label: 'Account Owner' },
        { value: 'ACCOUNT_MANAGER', label: 'Account Manager' },
        { value: 'ACCOUNT_USER', label: 'Account User' }
      ];
    } else if (currentUserRole === 'ACCOUNT_OWNER') {
      allowedRoles = [
        { value: 'ACCOUNT_MANAGER', label: 'Account Manager' },
        { value: 'ACCOUNT_USER', label: 'Account User' }
      ];
    } else if (currentUserRole === 'ACCOUNT_MANAGER') {
      allowedRoles = [
        { value: 'ACCOUNT_USER', label: 'Account User' }
      ];
    }

    return allowedRoles;
  }, [user]);

  const fetchEmployeeDetails = useCallback(async (employeeId) => {
    errorHandler.logApiCall('Fetching employee details...', employeeId);
    setIsLoading(true);
    try {
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await api.get(`/employees/${employeeId}?_t=${timestamp}`);
      
      // Debug the response structure
      errorHandler.logDebug('EmployeeCard', 'Employee details response:', response.data);
      
      if (response.data) {
        // Check if the response is wrapped in a data property
        const employeeData = response.data.data || response.data;
        
        setSelectedEmployee(employeeData);
        setOriginalEmployee(employeeData);
      } else {
        setSelectedEmployee(initialEmployeeState);
        setOriginalEmployee({});
      }
    } catch (err) {
      console.error('Error fetching employee details:', err);
      setSnackbar({
        open: true,
        message: 'Failed to fetch employee details: ' + (err.response?.data?.error || err.message || 'Unknown error'),
        severity: 'error'
      });
      setSelectedEmployee(initialEmployeeState);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchReviews = useCallback(async (employeeId) => {
    try {
      errorHandler.logDebug('EmployeeCard', `Fetching reviews for employee ${employeeId}`);
      const response = await api.get(`/reviews/employee/${employeeId}`);
      errorHandler.logDebug('EmployeeCard', 'Received reviews:', response.data);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setSnackbar({
        open: true,
        message: `Error fetching reviews: ${error.response?.data?.error || error.message}`,
        severity: 'error'
      });
    }
  }, []);

  // Create login for employee
  const handleCreateLogin = async () => {
    if (!selectedEmployee?.id) return;
    
    setLoginCreating(true);
    try {
      const response = await api.post(`/employees/${selectedEmployee.id}/create-login`);
      setLoginCredentials({
        email: response.data.email,
        temporary_password: response.data.temporary_password
      });
      
      // Update the selected employee to reflect they now have a login
      setSelectedEmployee(prev => ({ ...prev, has_login: true, user_id: response.data.user_id }));
      
      setSnackbar({
        open: true,
        message: 'Login created successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating login:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to create login',
        severity: 'error'
      });
      setLoginDialogOpen(false);
    } finally {
      setLoginCreating(false);
    }
  };

  const handleCopyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleCloseLoginDialog = () => {
    setLoginDialogOpen(false);
    setLoginCredentials(null);
    setCopiedField(null);
  };

  useEffect(() => {
    if (selectedEmployee?.id) {
      fetchReviews(selectedEmployee.id);
    }
  }, [selectedEmployee, fetchReviews]);

  useEffect(() => {
    errorHandler.logDebug('EmployeeCard', 'EmployeeCard component mounted');
    return () => {
      errorHandler.logDebug('EmployeeCard', 'EmployeeCard component unmounted');
    };
  }, []);

  useEffect(() => {
    errorHandler.logDebug('EmployeeCard', 'useEffect triggered - selectedEmployee:', selectedEmployee?.id, 'mode:', mode);
    if (selectedEmployee?.id && mode === 'View') {
      fetchEmployeeDetails(selectedEmployee.id);
    } else {
      setIsLoading(false);
    }
  }, [selectedEmployee?.id, mode, fetchEmployeeDetails]);
  
  // Check for URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchName = params.get('search');
    const mode = params.get('mode');
    
    if (searchName) {
      setSearchTerm(searchName);
      // Trigger search after a short delay to ensure component is ready
      setTimeout(() => {
        const searchButton = document.querySelector('button[type="submit"]') || 
                            document.querySelector('button:contains("Search")');
        // Or just call the search directly:
        api.get('/employees/search', { params: { query: searchName } })
          .then(response => {
            if (response.data.employees?.length > 0) {
              const employee = response.data.employees[0];
              setSelectedEmployee(employee);
              setOriginalEmployee(employee);
              setMode('View');
            }
          })
          .catch(err => console.error('Auto-search failed:', err));
      }, 100);
    }
    
    if (mode === 'new') {
      setMode('New');
    }
  }, []);

  // Replace the isEmployeeInMyAccount check with this enhanced version
  const isEmployeeInMyAccount = useMemo(() => {
    const employeeAccountId = selectedEmployee?.account_id;
    
    // Log the values being compared
    errorHandler.logDebug('EmployeeCard', 'Account comparison:', {
      employeeAccountId: employeeAccountId,
      userAccountId: userAccountId,
      user: user
    });
    
    // If both IDs exist, compare them
    if (employeeAccountId !== undefined && userAccountId !== null) {
      return String(employeeAccountId) === String(userAccountId);
    }
    
    // If we don't have the user's account ID yet, return false for security
    return false;
  }, [selectedEmployee?.account_id, userAccountId, user]);

  const handleSearch = async (event) => {
    event.preventDefault();
    setErrors({});
    errorHandler.logDebug('EmployeeCard', 'Searching for employee:', searchTerm);
    
    if (!searchTerm.trim()) {
      setErrors({ search: 'Please enter an employee name to search.' });
      return;
    }
    
    setIsLoading(true);
    try {
      // CRITICAL: Force refresh account ID before searching for security
      const currentAccountId = await fetchUserAccountId(true);
      console.log('Current account ID for search:', currentAccountId);
      
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await api.get('/employees/search', {
        params: { 
          query: searchTerm,
          _t: timestamp 
        }
      });
      
      // Debug the response structure
      errorHandler.logDebug('EmployeeCard', 'Search response structure:', {
        hasEmployees: !!response.data.employees,
        employeesLength: response.data.employees?.length,
        fullResponse: response.data
      });
      
      if (response.data.error) {
        setErrors({ search: response.data.error });
        setSelectedEmployee(initialEmployeeState);
      } else if (response.data.employees && response.data.employees.length > 0) {
        // The backend should already be filtering by account_id thanks to our security fixes
        // Get the first employee from the employees array
        const employee = response.data.employees[0];
        
        // Debug employee data before setting state
        errorHandler.logDebug('EmployeeCard', 'Found employee:', employee);
        errorHandler.logDebug('EmployeeCard', `User account ID: ${currentAccountId}, Employee account ID: ${employee.account_id}`);
        
        setSelectedEmployee(employee);
        setOriginalEmployee(employee);
        setMode('View');
        setSnackbar({ open: true, message: 'Employee found.', severity: 'success' });
      } else {
        setSelectedEmployee(initialEmployeeState);
        setSnackbar({ open: true, message: 'No employee found in your account.', severity: 'info' });
      }
    } catch (err) {
      console.error('Error searching employee:', err);
      setErrors({ search: 'An error occurred while fetching employee data.' });
      setSelectedEmployee(initialEmployeeState);
      setSnackbar({
        open: true,
        message: 'Error searching employee: ' + (err.response?.data?.error || err.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    errorHandler.logDebug('EmployeeCard', 'Save button clicked');
    errorHandler.logDebug('EmployeeCard', 'Current mode:', mode);
    errorHandler.logDebug('EmployeeCard', 'Selected employee:', selectedEmployee);
    errorHandler.logDebug('EmployeeCard', 'User role:', user?.role);

    setErrors({});
    try {
      await validationSchema.validate(selectedEmployee, { abortEarly: false });
      
      const personalInfoFields = [
        'first_name', 'last_name', 'name', 'email', 'role', 'job_role', 'tier_id', 'hourly_rate',
        'address_line1', 'address_line2', 'address_line3', 'postcode',
        'contact_number', 'emergency_contact', 'emergency_number',
        'date_of_birth', 'national_insurance_number', 'start_date',
        'active', 'commission_services', 'commission_products',
        'full_time_equivalent', 'generates_revenue'
      ];
      
      const updatedData = Object.fromEntries(
        Object.entries(selectedEmployee).filter(([key]) => personalInfoFields.includes(key))
      );

      // Backend derives the legacy `name` column from first_name + last_name,
      // so don't send a stale `name` value from a prior load that could
      // override what the backend computes.
      delete updatedData.name;

      // Send null rather than empty string for tier_id so the backend
      // stores NULL in the FK column rather than rejecting the value
      if (updatedData.tier_id === '' || updatedData.tier_id === undefined) {
        updatedData.tier_id = null;
      }

      let response;
      if (mode === 'New') {
        errorHandler.logDebug('EmployeeCard', 'Creating new employee...');
        response = await api.post('/employees', updatedData);
      } else if (mode === 'Update' && selectedEmployee?.id) {
        errorHandler.logDebug('EmployeeCard', 'Updating employee...', selectedEmployee.id);
        response = await api.put(`/employees/${selectedEmployee.id}`, updatedData);
      } else {
        throw new Error('Invalid mode or missing employee ID for update');
      }

      errorHandler.logApiCall('API response:', response);

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      // After successful update, explicitly refetch employee details
      if (mode === 'Update' && selectedEmployee?.id) {
        // Add small delay to ensure backend has processed the update
        setTimeout(() => {
          fetchEmployeeDetails(selectedEmployee.id);
        }, 500);
      } else if (response.data.id) {
        // For new employee, use the returned ID
        fetchEmployeeDetails(response.data.id);
      }
      
      setMode('View');
      setSnackbar({ 
        open: true, 
        message: mode === 'New' ? 'New employee created successfully' : 'Employee data updated successfully', 
        severity: 'success'
      });
    } catch (error) {
      console.error('Error in handleSave:', error);
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else {
        console.error('API Error:', error.response?.data);
        setSnackbar({ 
          open: true, 
          message: `Error: ${error.response?.data?.error || error.message || 'Unknown error'}`,
          severity: 'error' 
        });
      }
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setSelectedEmployee(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleModeChange = (newMode) => {
    errorHandler.logDebug('EmployeeCard', 'Mode changed to:', newMode);
    if (newMode === 'New') {
      setSelectedEmployee(initialEmployeeState);
    } else if (newMode === 'View') {
      setSelectedEmployee({ ...originalEmployee });
    }
    setMode(newMode);
    setErrors({});
  };

  const handleActiveStatusChange = (event) => {
    setSelectedEmployee(prev => ({ ...prev, active: event.target.value === 'active' }));
  };

  const handleViewFullReview = (review) => {
    setSelectedReview(review);
    setReviewMode('view');
    setOpenReviewDialog(true);
  };

  const handleNewReview = () => {
    setSelectedReview({
      employee_id: selectedEmployee.id,
      employee_name: selectedEmployee.name,
      // Initialize other fields as needed
    });
    setReviewMode('new');
    setOpenReviewDialog(true);
  };

  const handleEditReview = (review) => {
    setSelectedReview(review);
    setReviewMode('edit');
    setOpenReviewDialog(true);
  };

  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setSelectedReview(prev => ({ ...prev, [name]: value }));
  };

  const handleReviewCheckboxChange = (section, value) => {
    setSelectedReview(prev => {
      const updatedSection = prev[section].includes(value)
        ? prev[section].filter(item => item !== value)
        : [...prev[section], value];
      return { ...prev, [section]: updatedSection };
    });
  };

  const handleReviewSubmit = async () => {
    try {
      let response;
      if (reviewMode === 'new') {
        response = await api.post(`/reviews/employee/${selectedEmployee.id}`, selectedReview);
      } else if (reviewMode === 'edit') {
        response = await api.put(`/reviews/${selectedReview.id}`, selectedReview);
      }
      setSnackbar({ open: true, message: 'Review saved successfully', severity: 'success' });
      setOpenReviewDialog(false);
      fetchReviews(selectedEmployee.id);
    } catch (error) {
      console.error('Error saving review:', error);
      setSnackbar({ open: true, message: 'Failed to save review', severity: 'error' });
    }
  };

  const handleSaveDraft = async () => {
    try {
      const response = await api.post(`/reviews/employee/${selectedEmployee.id}/draft`, selectedReview);
      setSnackbar({ open: true, message: 'Draft saved successfully', severity: 'success' });
      setOpenReviewDialog(false);
      fetchReviews(selectedEmployee.id);
    } catch (error) {
      console.error('Error saving draft:', error);
      setSnackbar({ open: true, message: 'Failed to save draft', severity: 'error' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date) 
      ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : 'Invalid Date';
  };

  const isEditable = mode === 'New' || mode === 'Update';
  const canEditEmployee = user?.role === 'ACCOUNT_OWNER' || user?.role === 'ACCOUNT_MANAGER';

  // Modified function to show more debug info
  const getAccountMessage = () => {
    if (!selectedEmployee.id) return null;
    
    errorHandler.logDebug('EmployeeCard', 'Account message check:', {
      isEmployeeInMyAccount,
      employeeAccountId: selectedEmployee?.account_id,
      userAccountId
    });
    
    if (!isEmployeeInMyAccount) {
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This employee belongs to another account (ID: {selectedEmployee.account_id}). 
          You cannot view or edit their details.
          (Your account ID: {userAccountId || 'unknown'})
        </Alert>
      );
    }
    return null;
  };

  const isSaveButtonEnabled = () => {
    if (mode === 'New') {
      return canEditEmployee;
    } else if (mode === 'Update') {
      return isEditable && canEditEmployee && isEmployeeInMyAccount;
    }
    return false;
  };

  const renderReviewTab = () => (
    <Box>
      <Button onClick={handleNewReview} variant="contained" color="primary" sx={{ mb: 2 }}>
        New Review
      </Button>
      <Button onClick={() => fetchReviews(selectedEmployee.id)} variant="outlined" sx={{ ml: 2, mb: 2 }}>
        Refresh Reviews
      </Button>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Review Date</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>Performance Score</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((review) => (
              <TableRow key={review.id}>
                <TableCell>{formatDate(review.date_of_meeting)}</TableCell>
                <TableCell>{review.manager}</TableCell>
                <TableCell>{review.job_title}</TableCell>
                <TableCell>{review.performance_score}</TableCell>
                <TableCell>
                  <Button variant="outlined" onClick={() => handleViewFullReview(review)} sx={{ mr: 1 }}>
                    View
                  </Button>
                  <Button variant="outlined" onClick={() => handleEditReview(review)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openReviewDialog} onClose={() => setOpenReviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {reviewMode === 'view' ? 'View Review' : reviewMode === 'edit' ? 'Edit Review' : 'New Review'}
        </DialogTitle>
        <DialogContent>
          <ReviewForm 
            reviewData={selectedReview} 
            handleChange={handleReviewChange}
            handleCheckboxChange={handleReviewCheckboxChange}
            isReadOnly={reviewMode === 'view'}
          />
        </DialogContent>
        <DialogActions>
          {reviewMode !== 'view' && (
            <>
              <Button onClick={handleSaveDraft} color="primary">
                Save Draft
              </Button>
              <Button onClick={handleReviewSubmit} color="primary">
                Submit Review
              </Button>
            </>
          )}
          <Button onClick={() => setOpenReviewDialog(false)} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const renderTabContent = () => {
    errorHandler.logDebug('EmployeeCard', 'Rendering tab content', { activeTab, selectedEmployee });
    
    // Only show content if this is a new employee or it belongs to the user's account
    if (!selectedEmployee?.id && activeTab !== 'PersonalInfo' && mode !== 'New') {
      return <Typography>Please select an employee first.</Typography>;
    }

    // Don't display employee details if they don't belong to current account
    if (selectedEmployee?.id && !isEmployeeInMyAccount && mode !== 'New') {
      return <Typography>You cannot view details for employees from other accounts.</Typography>;
    }

    switch (activeTab) {
      case 'PersonalInfo':
        return (
          <PersonalInfo 
            employee={selectedEmployee} 
            isEditable={isEditable} 
            handleChange={handleChange}
            errors={errors}
            roleOptions={getRoleOptions()}
            tiers={tiers}
          />
        );
      case 'Schedule':
        return selectedEmployee?.id ? (
          <EmployeeScheduleEditor 
            employeeId={selectedEmployee.id} 
            employeeName={selectedEmployee.name}
          />
        ) : (
          <Typography color="text.secondary">
            Save the employee first to set their working schedule.
          </Typography>
        );
      case 'Holidays':
        return selectedEmployee?.id ? <Holidays employee={selectedEmployee} isEditable={isEditable} /> : null;
      case 'Absences':
        return selectedEmployee?.id ? <Absences employee={selectedEmployee} /> : null;
      case 'Training':
        return selectedEmployee?.id ? <TrainingManagement employee={selectedEmployee} isEditable={isEditable} /> : null;
      case 'Reviews':
        return renderReviewTab();
      case 'Disciplinary':
        return selectedEmployee?.id ? <Disciplinary employee={selectedEmployee} isEditable={isEditable} /> : null;
      case 'Notes':
        return selectedEmployee?.id ? <Notes employee={selectedEmployee} isEditable={isEditable} /> : null;
      default:
        return null;
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>Employee Card</Typography>
      
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Search employee by name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              error={!!errors.search}
              helperText={errors.search}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="contained" onClick={handleSearch}>Search</Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Mode</InputLabel>
              <Select
                value={mode}
                onChange={(e) => handleModeChange(e.target.value)}
                label="Mode"
              >
                <MenuItem value="New">New</MenuItem>
                <MenuItem value="View">View</MenuItem>
                <MenuItem value="Update">Update</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Display account warning if needed */}
      {getAccountMessage()}

      <Box sx={{ mb: 3 }}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Employee Status</FormLabel>
          <RadioGroup
            row
            name="active-status"
            value={selectedEmployee.active ? 'active' : 'inactive'}
            onChange={handleActiveStatusChange}
          >
            <FormControlLabel value="active" control={<Radio />} label="Active" disabled={!isEditable || !isEmployeeInMyAccount} />
            <FormControlLabel value="inactive" control={<Radio />} label="Inactive" disabled={!isEditable || !isEmployeeInMyAccount} />
          </RadioGroup>
        </FormControl>
      </Box>
      <Box sx={{ mb: 3, mt: 2 }}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Revenue Generator</FormLabel>
          <RadioGroup
            row
            name="generates-revenue"
            value={selectedEmployee.generates_revenue ? 'yes' : 'no'}
            onChange={(e) => handleChange({
              target: {
                name: 'generates_revenue',
                value: e.target.value === 'yes'
              }
            })}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" disabled={!isEditable || !isEmployeeInMyAccount} />
            <FormControlLabel value="no" control={<Radio />} label="No" disabled={!isEditable || !isEmployeeInMyAccount} />
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Login Status Section */}
      {selectedEmployee?.id && isEmployeeInMyAccount && (
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              System Login:
            </Typography>
            {selectedEmployee.has_login || selectedEmployee.user_id ? (
              <Chip 
                icon={<CheckCircleIcon />} 
                label="Has Login" 
                color="success" 
                size="small"
              />
            ) : (
              <>
                <Chip 
                  icon={<PersonIcon />} 
                  label="No Login" 
                  color="default" 
                  size="small"
                  sx={{ backgroundColor: '#ffecb3' }}
                />
                <Tooltip title={!selectedEmployee.email ? "Employee needs an email address first" : "Create login credentials for this employee"}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setLoginDialogOpen(true)}
                      disabled={!selectedEmployee.email}
                      sx={{ ml: 1 }}
                    >
                      Create Login
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
          {!selectedEmployee.email && !selectedEmployee.has_login && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Add an email address to enable login creation
            </Typography>
          )}
        </Box>
      )}

      {/* Employment contract: pre-filled from this employee's record and schedule */}
      {selectedEmployee?.id && isEmployeeInMyAccount && canEditEmployee && mode === 'View' && (
        <Box sx={{ mb: 3 }}>
          <Button variant="outlined" size="small" onClick={() => setContractOpen(true)}>
            Generate Contract
          </Button>
        </Box>
      )}

      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)} 
        sx={{ mb: 2 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Personal Info" value="PersonalInfo" />
        <Tab label="Schedule" value="Schedule" />
        <Tab label="Holidays" value="Holidays" />
        <Tab label="Absences" value="Absences" />
        <Tab label="Training" value="Training" />
        <Tab label="Reviews" value="Reviews" />
        <Tab label="Disciplinary" value="Disciplinary" />
        <Tab label="Notes" value="Notes" />
      </Tabs>

      <Box sx={{ mb: 3, opacity: selectedEmployee.active === false ? 0.5 : 1 }}>
        {renderTabContent()}
      </Box>

      <Box>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={!isSaveButtonEnabled()}
        >
          {mode === 'New' ? 'Create Employee' : 'Save Changes'}
        </Button>
        {!isSaveButtonEnabled() && selectedEmployee.id && !isEmployeeInMyAccount && (
          <Typography color="error" sx={{ mt: 1 }}>
            You cannot edit employees from other accounts.
          </Typography>
        )}
        {!isSaveButtonEnabled() && (
          <Typography color="error" sx={{ mt: 1 }}>
            {!isEditable ? "Not in editable mode" : "User doesn't have permission to edit"}
          </Typography>
        )}
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      )}

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ContractDialog
        open={contractOpen}
        employeeRef={selectedEmployee?.secure_id || selectedEmployee?.id}
        onClose={() => setContractOpen(false)}
      />

      {/* Create Login Dialog */}
      <Dialog 
        open={loginDialogOpen} 
        onClose={loginCredentials ? handleCloseLoginDialog : undefined}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {loginCredentials ? 'Login Created Successfully' : 'Create Employee Login'}
        </DialogTitle>
        <DialogContent>
          {!loginCredentials ? (
            <Box sx={{ py: 2 }}>
              <Typography gutterBottom>
                This will create a login for <strong>{selectedEmployee?.name}</strong> using their email address:
              </Typography>
              <Typography color="primary" sx={{ mb: 2, fontWeight: 500 }}>
                {selectedEmployee?.email}
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                A temporary password will be generated. You'll need to share it securely with the employee.
                They should change it on first login.
              </Alert>
            </Box>
          ) : (
            <Box sx={{ py: 2 }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                Login created! Share these credentials securely with the employee.
              </Alert>
              
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Email
              </Typography>
              <TextField
                fullWidth
                value={loginCredentials.email}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCopyToClipboard(loginCredentials.email, 'email')}>
                        {copiedField === 'email' ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />
              
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Temporary Password
              </Typography>
              <TextField
                fullWidth
                value={loginCredentials.temporary_password}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCopyToClipboard(loginCredentials.temporary_password, 'password')}>
                        {copiedField === 'password' ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                Make sure to copy these credentials now. The temporary password cannot be retrieved later.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!loginCredentials ? (
            <>
              <Button onClick={() => setLoginDialogOpen(false)} disabled={loginCreating}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateLogin} 
                variant="contained" 
                disabled={loginCreating}
                startIcon={loginCreating ? <CircularProgress size={20} /> : <PersonAddIcon />}
              >
                {loginCreating ? 'Creating...' : 'Create Login'}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseLoginDialog} variant="contained">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default EmployeeCard;